import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import Image from 'next/image';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { setError } from '../../../actions';
import {
  EndlessItemsPlaceholder,
  endlessItemsPlaceholderCards
} from '../../Placeholders/EndlessItemsPlaceholder';
import { ItemCard } from '../../ui/ItemCard';
import { getAllBookmarks } from '../../../api/queries';
import {
  BookmarksAllQuery,
  BookmarksAllQueryVariables
} from '../../../graphql/graphql';
import { withEndlessScroll } from '../../HOC/endlessScroll';
import { BookmarkList, EmptyBookmarks, NoResultText } from './styled';
import { PageWrapper } from '../../../styled/pageLayout.styles';
import { AdaptivePageHeader } from '../../ui/PageHeader/AdaptivePageHeader';

const mapDispatchToProps = (dispatch: Dispatch) => ({
  setErrorUI: (errorState: boolean) => dispatch(setError(errorState))
});

const INITIAL_BOOKMARKS_LIMIT = 50;
const BOOKMARKS_PAGE_LIMIT = 20;
const BOOKMARKS_PLACEHOLDER_COUNT = 10;

const BookmarksGrid: React.FC<{
  forwardedRef: React.RefObject<HTMLDivElement>;
  children?: React.ReactNode;
}> = ({ forwardedRef, children }) => (
  <BookmarkList ref={forwardedRef}>{children}</BookmarkList>
);

const EndlessBookmarks = withEndlessScroll<{ children?: React.ReactNode }>(
  BookmarksGrid
);

const BookmarksPage: React.FC<{
  setErrorUI: (arg: boolean) => void;
}> = ({ setErrorUI }) => {
  const { t } = useTranslation();
  const bookmarksListRef = useRef<HTMLDivElement>(null);
  const isLoadingMoreRef = useRef(false);
  const initialBatchSeenRef = useRef(false);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const { loading, data, error, fetchMore } = useQuery<
    BookmarksAllQuery,
    BookmarksAllQueryVariables
  >(getAllBookmarks, {
    variables: { limit: INITIAL_BOOKMARKS_LIMIT, offset: 0 },
    ssr: false,
    fetchPolicy: 'network-only'
  });

  const bookmarks = useMemo(() => data?.bookmarks || [], [data?.bookmarks]);

  useEffect(() => {
    if (!initialBatchSeenRef.current && data?.bookmarks) {
      initialBatchSeenRef.current = true;
      setHasMore(data.bookmarks.length >= INITIAL_BOOKMARKS_LIMIT);
    }
  }, [data?.bookmarks]);

  const onLoadMore = useCallback(() => {
    if (isLoadingMoreRef.current) return;
    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);

    fetchMore({
      variables: { limit: BOOKMARKS_PAGE_LIMIT, offset: bookmarks.length },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) return previousResult;
        return {
          ...previousResult,
          bookmarks: [...previousResult.bookmarks, ...fetchMoreResult.bookmarks]
        };
      }
    })
      .then(fetchMoreResult => {
        setHasMore(
          fetchMoreResult.data.bookmarks.length >= BOOKMARKS_PAGE_LIMIT
        );
      })
      .catch(() => {
        setErrorUI(true);
      })
      .finally(() => {
        isLoadingMoreRef.current = false;
        setIsLoadingMore(false);
      });
  }, [fetchMore, bookmarks.length, setErrorUI]);

  const emptyLayout = (
    <EmptyBookmarks data-cy="no-bookmarks">
      <Image
        src={'/static/images/favorites.svg'}
        width={150}
        height={276}
        alt="Image"
      />
      <NoResultText>{t('pages.bookmarks.no_results')}</NoResultText>
    </EmptyBookmarks>
  );

  const itemsList = useMemo(
    () =>
      bookmarks.map(bookmark => (
        <ItemCard
          key={bookmark.item.id}
          {...bookmark.item}
          inBookmarks={true}
        />
      )),
    [bookmarks]
  );

  useEffect(() => {
    if (error) setErrorUI(true);
  }, [error, setErrorUI]);

  return (
    <PageWrapper>
      <AdaptivePageHeader
        title={t('pages.bookmarks.title')}
        type="bookmarks"
        hasFilters={false}
      />
      {loading && (
        <EndlessItemsPlaceholder
          count={BOOKMARKS_PLACEHOLDER_COUNT}
          container={BookmarkList}
        />
      )}
      {!loading && !!itemsList.length && (
        <EndlessBookmarks
          ref={bookmarksListRef}
          items={itemsList}
          loading={isLoadingMore}
          loadingPlaceholder={endlessItemsPlaceholderCards(
            BOOKMARKS_PAGE_LIMIT
          )}
          isStopLoad={!hasMore}
          onLoadMore={onLoadMore}
        >
          {itemsList}
        </EndlessBookmarks>
      )}
      {!loading && !itemsList.length && (
        <BookmarkList $isEmptyView>{emptyLayout}</BookmarkList>
      )}
    </PageWrapper>
  );
};

export default connect(null, mapDispatchToProps)(BookmarksPage);
